import { useState, useEffect } from 'react';
import Api from 'entities/helpers/api';
import EverTrue from 'app';
import cadenceGqlCall from 'apps/cadences-v2/utils/cadence-gql-call';

/**
 * Derives a domain status string from the given domain object.
 * Requires both DKIM and Return-Path verification for 'configured' status.
 * @param {object|null} domain
 * @returns {'not_configured'|'pending'|'configured'}
 */
const getDomainStatus = (domain) => {
  if (!domain) return 'not_configured';
  if (domain.dkim_verified && domain.return_path_verified) return 'configured';
  return 'pending';
};

/**
 * Custom hook encapsulating all state and API calls for Postmark domain configuration.
 * Returns domain data, loading/error state, CRUD actions, and derived DNS record values.
 */
const usePostmarkDomain = () => {
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uiState, setUiState] = useState('idle'); // 'idle' | 'setup_form'

  const fetchDomain = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await Api.OUTREACH.POSTMARK_DOMAINS.get({
        success: (data) => data,
        error: () => null,
      });
      const results = resp?.items || resp || [];
      setDomain(Array.isArray(results) ? results[0] || null : null);
    } catch {
      setError('Failed to load domain configuration.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomain();
  }, []); // mount only

  /** Create a new postmark domain with an optional default sending email. */
  const createDomain = async (name, defaultEmail) => {
    const payload = { name };
    if (defaultEmail) payload.default_email = defaultEmail;
    const result = await Api.OUTREACH.POSTMARK_DOMAINS.post({
      data: JSON.stringify(payload),
      success: (data) => data,
      error: () => {
        EverTrue.Alert.error('Failed to create domain. Please try again.');
        return null;
      },
    });
    if (result) {
      setDomain(result);
      setUiState('idle');
      if (result.dkim_verified && result.return_path_verified) {
        EverTrue.Alert.success(`Domain ${name} verified successfully!`);
        cadenceGqlCall('mutation { notifyDnsVerified { resumed } }').catch(() => {});
      } else {
        EverTrue.Alert.success(`Domain ${name} configured. Add the DNS records below to your registrar.`);
      }
    }
    return result;
  };

  /** Delete the domain via DELETE, then notify et-cadences to pause affected cadences.
   *  Throws on API failure so callers can keep their confirmation modal open for retry. */
  const deleteDomain = async () => {
    if (!domain?.id) return;
    let succeeded = false;
    await Api.OUTREACH.POSTMARK_DOMAIN.delete({
      urlArgs: { id: domain.id },
      success: () => {
        succeeded = true;
      },
      error: () => {
        EverTrue.Alert.error('Failed to disconnect domain. Please try again.');
      },
    });
    if (!succeeded) throw new Error('Domain disconnect failed');
    setUiState('idle');
    let pausedCount = 0;
    try {
      const result = await cadenceGqlCall('mutation { notifyDnsDisconnected { paused } }');
      pausedCount = result?.notifyDnsDisconnected?.paused || 0;
    } catch {
      // Non-critical — cadences will be paused asynchronously
    }
    if (pausedCount > 0) {
      EverTrue.Alert.success(
        `DNS disconnected. ${pausedCount} cadence${pausedCount !== 1 ? 's' : ''} were automatically paused.`
      );
    } else {
      EverTrue.Alert.success('DNS configuration disconnected.');
    }
    await fetchDomain();
  };

  /** Trigger DKIM verification via PUT. Updates domain state on success. */
  const verifyDomain = async () => {
    if (!domain?.id) return;
    const result = await Api.OUTREACH.POSTMARK_DOMAIN_VERIFY.put({
      urlArgs: { id: domain.id },
      success: (data) => data,
      error: () => {
        EverTrue.Alert.error('Verification failed. Please check your DNS records and try again.');
        return null;
      },
    });
    if (result) {
      setDomain(result);
      if (result.dkim_verified && result.return_path_verified) {
        EverTrue.Alert.success('Domain verified successfully!');
        // Fire-and-forget: resume any cadences paused by the previous DNS disconnect
        cadenceGqlCall('mutation { notifyDnsVerified { resumed } }').catch(() => {});
      } else {
        EverTrue.Alert.error('DNS records not yet propagated. Please wait and try again.');
      }
    }
  };

  const status = getDomainStatus(domain);

  return {
    domain,
    loading,
    error,
    uiState,
    setUiState,
    status,
    createDomain,
    deleteDomain,
    verifyDomain,
  };
};

export default usePostmarkDomain;
